import { Dialog } from '../components/Dialog';
import { useI18n } from '../i18n';
import { displayName } from '../domain/names';
import { byId } from '../lib/grouped';
import { MemoryNodes } from '../features/photos/MemoryNodes';
import { WelcomeBoard } from '../features/welcome/WelcomeBoard';
import { PersonCard } from '../features/tree/PersonCard';
import { children as childrenOf, parents as parentsOf, partners as partnersOf } from '../domain/relationships';
import { useApp } from './AppState';
import { Lazily, lazyScreen } from './lazyScreen';

/*
 * What opens over the screen is fetched when it is first opened (and, before
 * that, in the background as soon as the app is idle — see lazyScreen.tsx). A
 * circle's constellation and its quick card are the exception: opening a person
 * is the one thing everybody does within seconds of arriving, so those come
 * with the app.
 */
const MeScreen = lazyScreen(() => import('../features/me/MeScreen').then((m) => ({ default: m.MeScreen })));
const SettingsScreen = lazyScreen(() => import('../features/me/SettingsScreen').then((m) => ({ default: m.SettingsScreen })));
const NotificationsScreen = lazyScreen(() => import('../features/me/SettingsScreen').then((m) => ({ default: m.NotificationsScreen })));
const ProfileScreen = lazyScreen(() => import('../features/people/ProfileScreen').then((m) => ({ default: m.ProfileScreen })));
const StoryDetailScreen = lazyScreen(() => import('../features/stories/StoryDetailScreen').then((m) => ({ default: m.StoryDetailScreen })));
const RecipeReader = lazyScreen(() => import('../features/recipes/RecipeReader').then((m) => ({ default: m.RecipeReader })));
const RecipeStudio = lazyScreen(() => import('../features/recipes/RecipeStudio').then((m) => ({ default: m.RecipeStudio })));

/**
 * Nothing in this app should cost you your place.
 *
 * Your profile, notifications and settings, a relative's quick card, their full
 * profile, a whole story, a recipe, and the pictures inside a day the family
 * remembers all open *on top of* wherever you are — the tree, the timeline,
 * another story — and closing any of them leaves you exactly there.
 *
 * They are real modals, so they stack in the order you opened them and the
 * browser keeps the newest one on top: a story from a circle, a face from
 * inside the story, that person's profile. The one exception is the quick card
 * and the profile, where "View profile" replaces the card rather than piling on
 * top of it — two depths of the same look at one person.
 */
export function Panels() {
  const { t, lang } = useI18n();
  const { panel, closePanel, personCardId, closePersonCard, profileId, closeProfile, storyId, closeStory, recipeId, closeRecipe, recipeEditor, memorySubject, memoryOrigin, closeMemories, peopleById, stories, me, graph } = useApp();
  const title = panel === 'settings' ? t('settings.title') : panel === 'profile' ? t('profile.myTitle') : t('notif.title');
  const person = profileId ? peopleById.get(profileId) : undefined;
  const carded = personCardId ? peopleById.get(personCardId) : undefined;
  const story = storyId ? byId(stories).get(storyId) : undefined;

  return (
    <>
      <Dialog open={panel !== null} onClose={closePanel} title={title} variant="panel" labelledBy="side-panel-title">
        <Lazily>
          {panel === 'settings' && <SettingsScreen />}
          {panel === 'profile' && <MeScreen />}
          {panel === 'notifications' && <NotificationsScreen />}
        </Lazily>
      </Dialog>

      {/* The full profile, over the screen you were on. */}
      <Dialog
        open={!!profileId}
        onClose={closeProfile}
        title={person ? displayName(person, lang) : t('profile.title')}
        variant="panel"
        labelledBy="profile-panel-title"
      >
        {profileId && <Lazily><ProfileScreen personId={profileId} inPanel /></Lazily>}
      </Dialog>

      {/*
        * A story, whole: its recordings, who is in it, its pictures, the
        * reaction and the two buttons — the same screen, over the one you were
        * on. There is no "open the story" step any more.
        */}
      <Dialog
        open={!!storyId}
        onClose={closeStory}
        title={story?.title ?? t('stories.title')}
        variant="panel"
        labelledBy="story-panel-title"
      >
        {storyId && <Lazily><StoryDetailScreen storyId={storyId} inPanel /></Lazily>}
      </Dialog>

      {/*
        * One day the family remembers — or one person — as a constellation: a
        * circle in the middle and everything belonging to it floating around,
        * with the plus that adds another. A bare dialog because the
        * constellation brings its own full-screen frame — but it still wants
        * the top layer, the scroll lock (so the tree or the timeline underneath
        * is exactly where you left it) and Escape.
        */}
      <Dialog open={!!memorySubject} onClose={closeMemories} title={t('memories.add')} variant="bare" panelClassName="is-nodes" labelledBy="memory-nodes-title">
        {/* Keyed by what it is about, so opening one *from* another replays the flight rather than swapping the contents. */}
        {memorySubject && <MemoryNodes key={`${memorySubject.kind}:${memorySubject.id}`} subject={memorySubject} origin={memoryOrigin} onClose={closeMemories} />}
      </Dialog>

      {/*
        * A recipe, to be cooked from. It is reached from the circle of whoever
        * cooks it (or wrote it down) and from nowhere else, so it is declared
        * after the constellation and opens over it — and the panel's own title
        * only says what kind of thing this is, because the page underneath opens
        * with the dish's name set the way a recipe's name should be.
        */}
      <Dialog open={!!recipeId} onClose={closeRecipe} title={t('recipe.one')} variant="panel" panelClassName="is-recipe" labelledBy="recipe-panel-title">
        {recipeId && <Lazily><RecipeReader recipeId={recipeId} /></Lazily>}
      </Dialog>

      {/*
        * Where a recipe is told, written up and put right. It brings its own
        * full-screen frame and its own way out (closing it has things to say —
        * "kept as a draft"), so it is its own dialog rather than one of these.
        * Keyed, so that leaving one recipe for another starts a clean editor.
        */}
      {recipeEditor && <Lazily quiet><RecipeStudio key={recipeEditor.recipeId ?? `new:${recipeEditor.personId ?? ''}`} request={recipeEditor} /></Lazily>}

      {/* The welcome message: up by itself when it is news to whoever is signed in, and from the menu whenever it is asked for. */}
      <WelcomeBoard />

      {/*
        * Who somebody is, in words: opened by **Details** on their constellation
        * and by a face on a story, a moment or a profile. Last in the file and
        * last opened, so it lands on top of whatever it came from — including
        * the constellation itself.
        */}
      {carded && (
        <PersonCard
          person={carded}
          onClose={closePersonCard}
          /* Your own circle with nobody joined to it yet: the card offers the way to fix that. */
          unconnected={!!me && carded.id === me.id && !hasRelatives(graph, me.id)}
          standalone
        />
      )}
    </>
  );
}

/** Whether anyone in the family is joined to this circle at all — read off their own three lists, not by asking everybody in the family in turn. */
function hasRelatives(graph: ReturnType<typeof useApp>['graph'], id: string): boolean {
  return parentsOf(graph, id).length > 0 || childrenOf(graph, id).length > 0 || partnersOf(graph, id).length > 0;
}
